import { useEffect, useRef } from 'react'
import { observer } from 'mobx-react-lite'
import cx from 'classnames'
import Env from '@/infra/env'
import type { TrustpilotPayload } from '@/features/jobs/details/ratings/TrustpilotApi'
import type { TrustpilotCollectorViewModel } from '@/features/jobs/details/ratings/TrustpilotCollectorViewModel'
import { useLightDarkFn } from '@/features/shell/theming/color-scheme-hooks'
import { isAbortError } from '@/utils/abort-util'
import styles from './TrustpilotCollector.module.css'

const scriptUrl =
  'https://reviewcollection.trustpilot.com/verifiedreviewcollector/main.js'
const templateId = '56278e9abfbbba0bdcd568bc'
let scriptPromise: Promise<void> | null = null

interface TrustpilotCollectorProps {
  vm: TrustpilotCollectorViewModel
  reference: string
  testId?: string
}

export const TrustpilotCollector = observer(function TrustpilotCollector({
  vm,
  reference,
  testId,
}: TrustpilotCollectorProps) {
  const elementRef = useRef<TrustpilotVerifiedReviewCollectorElement>(null)
  const lightDark = useLightDarkFn()
  const background = lightDark('light', 'dark')

  useEffect(() => {
    const controller = new AbortController()
    const element = elementRef.current

    vm.setTrustpilotState('loading')

    if (!element) {
      vm.setTrustpilotState('error')
      return () => controller.abort()
    }

    void Promise.all([
      loadTrustpilotScript(),
      vm.api.getPayload(reference, controller.signal),
    ])
      .then(async ([, payload]) => {
        if (controller.signal.aborted) {
          return
        }

        await waitForCollectorReady(
          element,
          AbortSignal.any([controller.signal, AbortSignal.timeout(15_000)]),
        )
        dispatchCustomerData(element, payload)
        vm.setTrustpilotState('ready')
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted || isAbortError(error)) {
          return
        }

        vm.setTrustpilotState('error')
      })

    return () => {
      controller.abort()
    }
  }, [background, reference, vm])

  return (
    <div
      className={cx(
        styles.collector,
        vm.trustpilotState !== 'ready' && styles.hidden,
      )}
    >
      <trustbox-verified-review-collector
        key={background}
        ref={elementRef}
        template-id={templateId}
        businessunit-id={Env.TRUSTPILOT_BUSINESS_UNIT_ID}
        locale="en-US"
        style-size="M"
        style-width="100%"
        border-type="shadow"
        background={background}
        has-animated-stars="true"
        tags="platform"
        data-test={testId}
      />
    </div>
  )
})

function waitForCollectorReady(
  element: TrustpilotVerifiedReviewCollectorElement,
  signal: AbortSignal,
) {
  return new Promise<void>((resolve, reject) => {
    if (signal.aborted) {
      reject(getAbortReason(signal))
      return
    }

    const onAbort = () => {
      element.onReady = noop
      reject(getAbortReason(signal))
    }

    signal.addEventListener('abort', onAbort, { once: true })
    element.onReady = () => {
      signal.removeEventListener('abort', onAbort)
      resolve()
    }
  })
}

function getAbortReason(signal: AbortSignal) {
  return signal.reason instanceof Error
    ? signal.reason
    : new DOMException('The operation was aborted', 'AbortError')
}

function noop() {
  return undefined
}

function dispatchCustomerData(
  element: TrustpilotVerifiedReviewCollectorElement,
  payload: TrustpilotPayload,
) {
  element.dispatchEvent(
    new CustomEvent<TrustpilotPayload>('customerDataResponse', {
      detail: payload,
    }),
  )
}

function loadTrustpilotScript() {
  if (customElements.get('trustbox-verified-review-collector')) {
    return Promise.resolve()
  }

  if (scriptPromise) {
    return scriptPromise
  }

  scriptPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script')
    script.src = scriptUrl
    script.async = true
    script.dataset.trustpilotCollector = 'true'
    script.addEventListener('load', () => resolve(), { once: true })
    script.addEventListener(
      'error',
      () => {
        script.remove()
        scriptPromise = null
        reject(new Error('Trustpilot collector failed to load'))
      },
      { once: true },
    )
    document.head.appendChild(script)
  })

  return scriptPromise
}
